import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { RefreshCw } from 'lucide-react';

// Polls the build manifest every 60s. If the main bundle hash changed,
// a new deployment happened — show a toast inviting the user to reload.
const POLL_INTERVAL_MS = 60_000;
const MANIFEST_URL = '/asset-manifest.json';

async function fetchCurrentBuildId() {
  try {
    const res = await fetch(`${MANIFEST_URL}?t=${Date.now()}`, {
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache' },
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data?.files?.['main.js'] || null;
  } catch {
    return null;
  }
}

export default function VersionChecker() {
  const initialBuildIdRef = useRef(null);
  const toastShownRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    let intervalId = null;

    (async () => {
      const initialId = await fetchCurrentBuildId();
      if (cancelled) return;
      initialBuildIdRef.current = initialId;

      const check = async () => {
        if (toastShownRef.current) return;
        const latestId = await fetchCurrentBuildId();
        if (!latestId || !initialBuildIdRef.current) return;
        if (latestId !== initialBuildIdRef.current) {
          toastShownRef.current = true;
          toast.message(
            'Nouvelle version disponible',
            {
              description: 'Cliquez pour recharger et obtenir la dernière version de FixFlow.',
              icon: <RefreshCw className="w-4 h-4" />,
              duration: Infinity,
              action: {
                label: 'Recharger',
                onClick: () => {
                  if (window.caches?.keys) {
                    window.caches.keys().then((keys) => {
                      keys.forEach((k) => window.caches.delete(k));
                    }).finally(() => window.location.reload());
                  } else {
                    window.location.reload();
                  }
                },
              },
            }
          );
        }
      };

      intervalId = setInterval(check, POLL_INTERVAL_MS);
    })();

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, []);

  return null;
}
